import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  MenuBook as ComicIcon,
  Person as PersonIcon,
  Description as ScriptIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ComicBookService } from '../services/comicBookService';
import {
  ComicSession,
  ComicSessionStorage,
  StartComicSessionRequest,
  CharacterInfo,
  COMIC_GENRES,
  COMIC_STYLES
} from '../models/comicBook';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import { SessionWithAssets } from '../services/assetRetrievalService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`comic-tabpanel-${index}`}
      aria-labelledby={`comic-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const ComicBookAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0); // Start with Create New tab
  const [comicSubTab, setComicSubTab] = useState(0);
  const [title, setTitle] = useState('');
  const [conceptDescription, setConceptDescription] = useState('');
  const [genre, setGenre] = useState('');
  const [style, setStyle] = useState('');
  const [issueCount, setIssueCount] = useState(1);
  const [generateNarrativeArc, setGenerateNarrativeArc] = useState(true);
  const [characters, setCharacters] = useState<CharacterInfo[]>([]);
  const [characterDialog, setCharacterDialog] = useState(false);
  const [newCharacterName, setNewCharacterName] = useState('');
  const [newCharacterDescription, setNewCharacterDescription] = useState('');
  const [newCharacterRole, setNewCharacterRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [narrative, setNarrative] = useState('');
  const [script, setScript] = useState('');
  const [sessions, setSessions] = useState<ComicSession[]>([]);

  // Asset retrieval hook for backend data
  const { sessions: backendSessions, loading: backendLoading, refetch: refetchBackendData } = useAssetRetrieval({
    agentType: 'ComicBookSession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true
  });

  // Merge backend sessions with local sessions
  useEffect(() => {
    const stored = localStorage.getItem('comicBookSessions');
    let localSessions: ComicSession[] = [];

    if (stored) {
      const data: ComicSessionStorage = JSON.parse(stored);
      localSessions = data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      }));
    }

    // Convert backend sessions to ComicSession format
    const convertedBackendSessions: ComicSession[] = backendSessions.map((backendSession: SessionWithAssets) => {
      // Parse sessionData JSON string to extract comic data
      let sessionData: any = {};
      try {
        if (backendSession.session.sessionData) {
          sessionData = JSON.parse(backendSession.session.sessionData);
        }
      } catch (e) {
        console.error('[ComicBookAgent] Failed to parse sessionData:', e);
      }

      return {
        id: backendSession.session.id,
        sessionId: backendSession.session.id,
        title: sessionData.Title || backendSession.session.title || backendSession.session.metadata?.title || 'Untitled Comic',
        conceptDescription: sessionData.ConceptDescription || backendSession.session.metadata?.conceptDescription || '',
        genre: sessionData.Genre || backendSession.session.genre || backendSession.session.metadata?.genre,
        style: sessionData.Style || backendSession.session.style || backendSession.session.metadata?.style,
        issueCount: sessionData.Issues?.length || backendSession.session.metadata?.issueCount || 1,
        characters: sessionData.Characters || backendSession.session.metadata?.characters || [],
        narrative: sessionData.NarrativeArc || backendSession.assets.find(a => a.assetType === 'narrative')?.textContent || '',
        script: sessionData.Issues?.[0]?.GeneratedScript || backendSession.assets.find(a => a.assetType === 'script')?.textContent,
        issues: sessionData.Issues || [],
        createdAt: new Date(backendSession.session.createdAt)
      };
    });

    // Merge: backend sessions + local-only sessions (filter out duplicates by id)
    const backendIds = new Set(convertedBackendSessions.map(s => s.id));
    const uniqueLocalSessions = localSessions.filter(s => !backendIds.has(s.id));
    const merged = [...convertedBackendSessions, ...uniqueLocalSessions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setSessions(merged);
  }, [backendSessions]);

  // Save sessions to localStorage
  const saveSessions = (newSessions: ComicSession[]) => {
    const storage: ComicSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('comicBookSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleAddCharacter = () => {
    if (newCharacterName.trim()) {
      const newCharacter: CharacterInfo = {
        name: newCharacterName.trim(),
        description: newCharacterDescription.trim() || undefined,
        role: newCharacterRole.trim() || undefined
      };
      setCharacters([...characters, newCharacter]);
      setNewCharacterName('');
      setNewCharacterDescription('');
      setNewCharacterRole('');
      setCharacterDialog(false);
    }
  };

  const handleRemoveCharacter = (index: number) => {
    setCharacters(characters.filter((_, i) => i !== index));
  };

  const handleStartSession = async () => {
    if (!title.trim() || !conceptDescription.trim()) {
      setError('Please provide a title and concept description');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: StartComicSessionRequest = {
        title: title.trim(),
        conceptDescription: conceptDescription.trim(),
        genre: genre || undefined,
        style: style || undefined,
        initialCharacters: characters.length > 0 ? characters : undefined,
        issueCount: issueCount,
        generateNarrativeArc
      };

      const response = await ComicBookService.startSession(request);
      
      if (response.sessionId) {
        setSessionId(response.sessionId);
        setNarrative(response.narrativeArc || '');
        setTabValue(1); // Switch to Current Comic tab
        setComicSubTab(1); // Go to Narrative Arc sub-tab
        
        // Save the initial session with narrative arc
        const newSession: ComicSession = {
          id: Date.now().toString(),
          sessionId: response.sessionId,
          title: title.trim(),
          conceptDescription: conceptDescription.trim(),
          genre: genre || undefined,
          style: style || undefined,
          issueCount,
          characters: characters.length > 0 ? characters : undefined,
          narrative: response.narrativeArc || '',
          createdAt: new Date()
        };

        saveSessions([newSession, ...sessions]);
        await refetchBackendData(); // Refresh backend data
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start comic book session');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateScript = async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await ComicBookService.generateScript(sessionId, 1);
      
      if (response.script) {
        setScript(response.script);
        setComicSubTab(2); // Go to Script sub-tab
        
        // Update existing session with script
        const updatedSessions = sessions.map(session => 
          session.sessionId === sessionId 
            ? { ...session, script: response.script }
            : session
        );
        saveSessions(updatedSessions);
        await refetchBackendData(); // Refresh backend data
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate script');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDownload = () => {
    const content = `# ${title}

## Concept
${conceptDescription}

${genre ? `**Genre:** ${genre}\n` : ''}
${style ? `**Style:** ${style}\n` : ''}
**Issues:** ${issueCount}

## Characters
${characters.map(char => `
### ${char.name}
${char.role ? `**Role:** ${char.role}` : ''}
${char.description || ''}
`).join('\n')}

## Narrative Arc
${narrative}

## Script - Issue #1
${script}
`;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '-').toLowerCase()}-comic-script.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadSession = (session: ComicSession) => {
    setTitle(session.title);
    setConceptDescription(session.conceptDescription || '');
    setGenre(session.genre || '');
    setStyle(session.style || '');
    setIssueCount(session.issueCount || 1);
    setCharacters(session.characters || []);
    setScript(session.script || '');
    setNarrative(session.narrative || '');
    setSessionId(session.sessionId || null);
    setTabValue(1); // Switch to Current Comic tab
    setComicSubTab(0); // Start with Overview
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
  };

  const handleReset = () => {
    setTitle('');
    setConceptDescription('');
    setGenre('');
    setStyle('');
    setIssueCount(1);
    setCharacters([]);
    setSessionId(null);
    setNarrative('');
    setScript('');
    setComicSubTab(0);
    setGenerateNarrativeArc(true);
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 1, sm: 2 }, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <ComicIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Comic Creator' : 'Comic Book Creator'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'AI-powered comic scripts' : 'Create complete comic book scripts with AI-powered narrative development'}
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>

        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ 
            mb: 2,
            '& .MuiTab-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 48
            }
          }}
          variant={isMobile ? 'fullWidth' : 'standard'}
        >
          <Tab label={isMobile ? 'Create' : 'Create New'} />
          <Tab label={isMobile ? 'Comic' : 'Current Comic'} disabled={!sessionId} />
          <Tab label="Sessions" />
        </Tabs>

        {/* Create New Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 4 }, maxWidth: 600, mx: 'auto' }}>
            <TextField
              fullWidth
              label={isMobile ? 'Title' : 'Comic Title'}
              placeholder={isMobile ? 'e.g., The Amazing Adventures...' : 'e.g., The Amazing Adventures of...'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              label={isMobile ? 'Concept' : 'Concept Description'}
              placeholder={isMobile ? 'Describe your comic...' : 'Describe your comic book concept, setting, and main story idea...'}
              value={conceptDescription}
              onChange={(e) => setConceptDescription(e.target.value)}
              required
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Genre
                </InputLabel>
                <Select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  label="Genre"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                  {COMIC_GENRES.map((g) => (
                    <MenuItem key={g} value={g} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {isMobile ? 'Style' : 'Art Style'}
                </InputLabel>
                <Select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  label={isMobile ? 'Style' : 'Art Style'}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                  {COMIC_STYLES.map((s) => (
                    <MenuItem key={s} value={s} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                type="number"
                label={isMobile ? '# Issues' : 'Number of Issues'}
                value={issueCount}
                onChange={(e) => setIssueCount(parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 12 }}
                sx={{ width: { xs: '100%', sm: 200 } }}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={generateNarrativeArc}
                    onChange={(e) => setGenerateNarrativeArc(e.target.checked)}
                  />
                }
                label={isMobile ? 'Narrative arc' : 'Generate narrative arc'}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Box>

            <Box>
              <Typography 
                variant="subtitle1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Characters (Opt.)' : 'Characters (Optional)'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setCharacterDialog(true)}
                  startIcon={!isMobile && <AddIcon />}
                  sx={{ borderRadius: 1.5, minWidth: { xs: 120, sm: 140 }, minHeight: 44 }}
                >
                  {isMobile ? 'Add' : 'Add Character'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {characters.map((char, index) => (
                  <Chip
                    key={index}
                    label={`${char.name}${char.role ? ` (${char.role})` : ''}`}
                    onDelete={() => handleRemoveCharacter(index)}
                    deleteIcon={<CloseIcon />}
                    sx={{ 
                      borderRadius: 1.5,
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }}
                  />
                ))}
              </Box>
              {characters.length === 0 && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontStyle: 'italic',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {isMobile ? 'Add characters later' : 'Characters can be developed as you create the comic'}
                </Typography>
              )}
            </Box>

            {error && (
              <Alert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, justifyContent: 'center', pt: { xs: 2, sm: 3 } }}>
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                onClick={handleStartSession}
                disabled={loading || !title.trim() || !conceptDescription.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <AddIcon />)}
                sx={{ 
                  borderRadius: 2, 
                  px: { xs: 3, sm: 4 }, 
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                }}
              >
                {loading ? (isMobile ? 'Creating...' : 'Creating Comic...') : (isMobile ? 'Create' : 'Create Comic Book')}
              </Button>
            </Box>
          </Box>
        </TabPanel>

        {/* Current Comic Tab */}
        <TabPanel value={tabValue} index={1}>
          {sessionId && (narrative || script) ? (
            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
              {/* Comic Overview */}
              <Card sx={{ mb: { xs: 2, sm: 3 } }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    {title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    paragraph
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {conceptDescription}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
                    {genre && <Chip label={isMobile ? genre : `Genre: ${genre}`} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />}
                    {style && <Chip label={isMobile ? style : `Style: ${style}`} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />}
                    <Chip label={`${issueCount} Issue${issueCount > 1 ? 's' : ''}`} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                    {characters.length > 0 && <Chip label={`${characters.length} ${isMobile ? 'Chars' : 'Characters'}`} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />}
                  </Box>
                </CardContent>
              </Card>

              {/* Narrative Arc */}
              {narrative && (
                <Card sx={{ mb: { xs: 2, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'Narrative' : 'Narrative Arc'}
                      </Typography>
                      <IconButton 
                        onClick={() => handleCopy(narrative)} 
                        size="small"
                        sx={{ minWidth: 44, minHeight: 44 }}
                        title={isMobile ? 'Copy' : 'Copy narrative'}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: 'background.default', 
                      p: { xs: 1.5, sm: 2 }, 
                      borderRadius: 1,
                      maxHeight: { xs: '300px', sm: '400px' },
                      overflow: 'auto',
                      border: 1,
                      borderColor: 'divider',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}>
                      <ReactMarkdown>{narrative}</ReactMarkdown>
                    </Box>
                    {!script && (
                      <Box sx={{ mt: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                        <Button
                          variant="contained"
                          onClick={handleGenerateScript}
                          disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <ScriptIcon />)}
                          sx={{ 
                            borderRadius: 2,
                            minHeight: 44,
                            px: { xs: 2.5, sm: 3 },
                            fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                          }}
                        >
                          {loading ? (isMobile ? 'Generating...' : 'Generating Script...') : (isMobile ? 'Generate Script' : 'Generate Script')}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Comic Script */}
              {script && (
                <Card>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'Script #1' : 'Comic Script - Issue #1'}</Typography>
                      <Box>
                        <IconButton 
                          onClick={() => handleCopy(script)} 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                          title={isMobile ? 'Copy' : 'Copy script'}
                        >
                          <CopyIcon />
                        </IconButton>
                        <IconButton 
                          onClick={handleDownload} 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                          title={isMobile ? 'Download' : 'Download script'}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: 'background.default', 
                      p: { xs: 1.5, sm: 2, md: 3 }, 
                      borderRadius: 1,
                      maxHeight: { xs: '400px', sm: '600px' },
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      border: 1,
                      borderColor: 'divider'
                    }}>
                      <ReactMarkdown>{script}</ReactMarkdown>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
              <ComicIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No comic' : 'No active comic session'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Create a new comic' : 'Create a new comic book to start working'}
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Sessions Tab */}
        <TabPanel value={tabValue} index={2}>
          {sessions.length > 0 ? (
            <List>
              {sessions.map((session) => (
                <ListItem 
                  key={session.id} 
                  divider
                  sx={{ 
                    cursor: 'pointer', 
                    borderRadius: 1, 
                    mb: 1, 
                    py: { xs: 1.5, sm: 2 },
                    '&:hover': { backgroundColor: 'action.hover' } 
                  }}
                  onClick={() => handleLoadSession(session)}
                >
                  <ListItemText
                    primary={
                      <Typography 
                        variant="h6" 
                        component="div"
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {session.title}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: { xs: 0.5, sm: 1 } }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                        >
                          {session.genre && `${session.genre} • `}
                          {session.style && `${session.style} • `}
                          {session.issueCount || 1} {isMobile ? 'iss' : 'issue'}{(session.issueCount || 1) > 1 ? '' : ''} • 
                          {session.characters?.length || 0} char{(session.characters?.length || 0) !== 1 && !isMobile ? 'acters' : 's'}
                          {session.narrative && ' • Narrative'}
                          {session.script && ' • Script'}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          {isMobile ? '' : 'Created: '}{session.createdAt.toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={isMobile ? 'Del' : 'Delete'}>
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
              <ComicIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No comics yet' : 'No comic books created yet'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Projects will appear here' : 'Your comic book projects will appear here'}
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Box>
      </Paper>

      {/* Character Dialog */}
      <Dialog 
        open={characterDialog} 
        onClose={() => setCharacterDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          {isMobile ? 'Add Character' : 'Add Character'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
            <TextField
              fullWidth
              label={isMobile ? 'Name' : 'Character Name'}
              value={newCharacterName}
              onChange={(e) => setNewCharacterName(e.target.value)}
              autoFocus
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              label="Role"
              placeholder={isMobile ? 'e.g., Hero' : 'e.g., Hero, Villain, Sidekick'}
              value={newCharacterRole}
              onChange={(e) => setNewCharacterRole(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Description"
              placeholder={isMobile ? 'Brief description...' : 'Brief character description...'}
              value={newCharacterDescription}
              onChange={(e) => setNewCharacterDescription(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setCharacterDialog(false)}
            sx={{ 
              minHeight: 44,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCharacter} 
            variant="contained" 
            disabled={!newCharacterName.trim()}
            sx={{ 
              minHeight: 44,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            {isMobile ? 'Add' : 'Add Character'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ComicBookAgent;
